import {
	type CSSProperties,
	type ReactNode,
	useEffect,
	useRef,
	useState,
} from 'react'
import { Link } from 'react-router'
import { MediaImage } from '#app/components/image.tsx'
import {
	createWebPageSchema,
	organizationSchema,
	webSiteSchema,
} from '#app/utils/constants.ts'
import { mediaAsset, mediaUrl } from '#app/utils/media.ts'
import { type Route } from './+types/index.ts'

export const handle = { chrome: 'payload' }

const MetaTitle = 'Car Dealer Websites & Automotive SEO | DealerProEdge'
const MetaDescription =
	'DealerProEdge builds fast, conversion-focused car dealer websites with inventory integrations, automotive SEO, and tools that turn more shoppers into leads.'
const SocialImage = mediaAsset('/images/dealerproedge-social-preview.webp')

export const meta: Route.MetaFunction = ({ matches }) => {
	const rootData = matches[0]?.data as
		| { requestInfo?: { origin: string; path: string } }
		| undefined
	const origin = rootData?.requestInfo?.origin ?? 'https://dealerproedge.com'
	const path = rootData?.requestInfo?.path ?? '/'
	const canonicalUrl = `${origin}${path}`
	const pageSchema = createWebPageSchema({
		name: MetaTitle,
		description: MetaDescription,
		url: canonicalUrl,
	})

	return [
		{ title: MetaTitle },
		{ name: 'description', content: MetaDescription },
		{ name: 'twitter:title', content: MetaTitle },
		{ name: 'twitter:description', content: MetaDescription },
		{ name: 'twitter:image', content: SocialImage },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ property: 'og:title', content: MetaTitle },
		{ property: 'og:description', content: MetaDescription },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: canonicalUrl },
		{ property: 'og:image', content: SocialImage },
		{ property: 'og:site_name', content: 'DealerProEdge' },
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
		{ 'script:ld+json': organizationSchema },
		{ 'script:ld+json': webSiteSchema },
		{ 'script:ld+json': pageSchema },
	]
}

const videoPath = '/images/payload-home'
const logoShowcaseCellCount = 6

type NavColumn = {
	hideLabel?: boolean
	label: string
	links: { href: string; label: string }[]
}

type NavItem = {
	description?: string
	featured?: {
		actions?: { href: string; label: string }[]
		eyebrow: string
		title: string
	}
	href: string
	label: string
	sections?: NavColumn[]
	visualTiles?: { label: string }[]
}

const navItems: NavItem[] = [
	{
		description: 'Performance-first products for dealership growth.',
		featured: {
			actions: [{ href: '/book-demo', label: 'Schedule a Demo' }],
			eyebrow: 'DealerProEdge Platform',
			title:
				'Fast websites, inventory-aware SEO, and messaging tools built for dealers.',
		},
		href: '/car-dealer-websites',
		label: 'Products',
		sections: [
			{
				label: 'Services',
				links: [
					{ href: '/car-dealer-websites', label: 'Dealer Websites' },
					{ href: '/automotive-seo', label: 'Automotive SEO' },
					{ href: '/chat-messaging', label: 'Chat & Messaging' },
				],
			},
			//TODO: Re-enable the Features section under Product once we have the chance to flesh out the feature pages
			// {
			// 	label: 'Features',
			// 	links: [
			// 		{ href: '#inventory-sync', label: 'Inventory Sync' },
			// 		{ href: '#lead-capture', label: 'Lead Capture' },
			// 		{ href: '#performance-dashboard', label: 'Performance Dashboard' },
			// 	],
			// },
		],
	},
	//TODO: Re-enable the Industries nav item once we have the chance to flesh out the industry pages
	// {
	// 	description: 'Dealer-focused website paths for automotive, marine, powersports, and motorcycle retailers.',
	// 	featured: {
	// 		actions: [{ href: '/book-demo', label: 'Schedule a Demo' }],
	// 		eyebrow: 'Industries',
	// 		title: 'Purpose-built website paths for the dealerships and dealer groups we serve.',
	// 	},
	// 	href: '/industries/automotive-dealers',
	// 	label: 'Industries',
	// 	sections: [
	// 		{
	// 			label: 'Dealer Types',
	// 			links: [
	// 				{ href: '/industries/automotive-dealers', label: 'Automotive Dealers' },
	// 				{
	// 					href: '/industries/powersports-marine-dealers',
	// 					label: 'Powersports & Marine Dealers',
	// 				},
	// 				{ href: '/industries/motorcycle-dealers', label: 'Motorcycle Dealers' },
	// 			],
	// 		},
	// 	],
	// 	visualTiles: [{ label: 'RVs' }, { label: 'Motorcycles' }, { label: 'Cars' }],
	// },
	//TODO: Re-enable the Resources dropdown once we decide how to surface it again
	// {
	// 	description:
	// 		'The team, support, and comparison guides behind DealerProEdge.',
	// 	featured: {
	// 		actions: [{ href: '/book-demo', label: 'Book a Demo' }],
	// 		eyebrow: 'Resources',
	// 		title:
	// 			'A practical partner for dealerships that want cleaner technology and clearer results.',
	// 	},
	// 	href: '/about-us',
	// 	label: 'Resources',
	// 	sections: [
	// 		{
	// 			hideLabel: true,
	// 			label: 'Company',
	// 			links: [
	// 				{ href: '/about-us', label: 'About Us' },
	// 				{ href: '/blog', label: 'Blog' },
	// 				{ href: '/support', label: 'Support' },
	// 			],
	// 		},
	// 		//TODO: ADD BACK Later
	// 		// {
	// 		// 	label: 'Why Us',
	// 		// 	links: [
	// 		// 		{
	// 		// 			href: '/why-us/dpe-vs-dealeron',
	// 		// 			label: 'Dealer Pro Edge vs DealerOn',
	// 		// 		},
	// 		// 		{
	// 		// 			href: '/why-us/dpe-vs-motive',
	// 		// 			label: 'Dealer Pro Edge vs Motive',
	// 		// 		},
	// 		// 	],
	// 		// },
	// 	],
	// },
	{ href: '/blog', label: 'Blog' },
	{ href: '/support', label: 'Support' },
	{ href: '/about-us', label: 'About Us' },
	{ href: '/success-and-guarantee', label: 'Success & Guarantee' },
]

const logoShowcase = [
	{
		name: 'HPA',
		src: mediaAsset('/images/payload-home/HPA Logo Transparent.png'),
		alt: 'HPA logo',
		className: 'dpe-live-logo-hpa',
	},
	{
		name: 'Hitchcock Motor Company',
		src: mediaAsset('/images/payload-home/hitchockmotorlogos-dark.webp'),
		alt: 'Hitchcock Motor Company logo',
		className: 'dpe-live-logo-hitchcock',
	},
	{
		name: 'Blasius North',
		src: mediaUrl('DealerLogos/BlasiusNorth.png'),
		alt: 'Blasius North logo',
	},
	{
		name: 'Blasius South',
		src: mediaUrl('DealerLogos/BlasiusSouth.png'),
		alt: 'Blasius South logo',
	},
	{
		name: 'Blasius Pre-Owned',
		src: mediaUrl('DealerLogos/BlasiusPreowned.png'),
		alt: 'Blasius Pre-Owned logo',
	},
	{
		name: 'Blasius of Bristol',
		src: mediaAsset('/images/payload-home/Blasius of Bristol.png'),
		alt: 'Blasius of Bristol logo',
		className: 'dpe-live-logo-bristol',
	},
	// {
	// 	name: 'Blasius Mitsubishi',
	// 	src: `${dealerLogoBaseUrl}/Blasius-Mitsubishi-Logo.avif`,
	// 	alt: 'Blasius Mitsubishi logo',
	// },
	// {
	// 	name: 'Blasius Cadillac',
	// 	src: `${dealerLogoBaseUrl}/Blasius-Cadillac-Logo.jpg`,
	// 	alt: 'Blasius Cadillac logo',
	// },
	// {
	// 	name: 'Blasius Chevrolet',
	// 	src: `${dealerLogoBaseUrl}/Blasius-Chevrolet-Logo.jpg`,
	// 	alt: 'Blasius Chevrolet logo',
	// },
	// {
	// 	name: 'Blasius GMC',
	// 	src: `${dealerLogoBaseUrl}/BlasiusGmc-Logo.avif`,
	// 	alt: 'Blasius GMC logo',
	// },
]

type LogoShowcaseItem = (typeof logoShowcase)[number]

function isSvgMedia(src: string) {
	return /\.svg(?:\?|$)/i.test(src)
}

type UseCaseItem = {
	alt: string
	bottomAlt?: string
	bottomImage?: string
	href: string
	title: string
	topImage?: string
}

const useCases: UseCaseItem[] = [
	{
		title: 'AI Chat',
		href: '/chat-messaging',
		bottomImage: mediaAsset('/images/payload-home/Ai Chat.png'),
		alt: 'DealerProEdge AI chat interface for dealership shoppers.',
	},
	{
		title: 'AI-Powered Search',
		href: '/car-dealer-websites',
		bottomImage: mediaAsset('/images/payload-home/AI Search.png'),
		alt: 'AI-powered vehicle inventory search results.',
	},
	{
		title: 'Rich AI Description',
		href: '/automotive-seo',
		bottomImage: mediaAsset('/images/payload-home/Ai Description.png'),
		alt: 'DealerProEdge AI-assisted vehicle description editor.',
	},
]

const futureCards = [
	{
		title: 'Dealer CMS',
		text: 'With reusable content, customizable templates, hourly content backups, and unmatched reliability.',
		image: 'DPE Backend.png',
		alt: 'DealerProEdge content management dashboard on a laptop',
	},
	{
		title: 'AI Assistant',
		text: 'Let AI augment your content, not drive it. Highlight different text and receive alternate suggestions, or rewrite copy effortlessly.',
		image: 'writing-assistant-light-updated.webp',
		alt: 'AI writing assistant suggesting alternate copy',
	},
	{
		title: 'Video Embedding',
		text: "We monitor your dealership's YouTube channels and automatically embed videos on your VDPs—at no extra cost.",
		image: 'backend video.png',
		alt: 'backend video embedding interface',
	},
]

const footerColumns = [
	{
		label: 'Product',
		links: [
			{ href: '/car-dealer-websites', label: 'Dealer Websites' },
			{ href: '/automotive-seo', label: 'Automotive SEO' },
			{ href: '/chat-messaging', label: 'Chat & Messaging' },
		],
	},
	{
		label: 'Company',
		links: [
			{ href: '/compare', label: 'Compare Providers' },
			{ href: '/about-us', label: 'About Us' },
			{ href: '/support', label: 'Support' },
			{ href: '/sitemap', label: 'Sitemap' },
		],
	},
	{
		label: 'Proof',
		links: [
			{ href: '/success-and-guarantee', label: 'Success & Guarantee' },
			{ href: '/limited-time-offer', label: 'Limited Time Offer' },
		],
	},
]

function SmartLink({
	children,
	className,
	href,
	id,
	linkRef,
	newTab,
	onClick,
}: {
	children: ReactNode
	className?: string
	href: string
	id?: string
	linkRef?: (ref: HTMLAnchorElement | null) => void
	newTab?: boolean
	onClick?: () => void
}) {
	if (href.startsWith('http')) {
		return (
			<a
				className={className}
				href={href}
				id={id}
				onClick={onClick}
				ref={linkRef}
				rel={newTab ? 'noopener noreferrer' : undefined}
				target={newTab ? '_blank' : undefined}
			>
				{children}
			</a>
		)
	}

	return (
		<Link
			className={className}
			id={id}
			onClick={onClick}
			ref={linkRef}
			to={href}
		>
			{children}
		</Link>
	)
}

function PayloadLogo({ className = 'dpe-live-logo' }: { className?: string }) {
	return (
		<MediaImage
			alt="Dealer Pro Edge Logo"
			className={`${className} dpe-live-dealer-logo`}
			height={150}
			layout="constrained"
			src="/app-assets/logo/logo.png"
			width={1172}
		/>
	)
}

function Arrow() {
	return (
		<span aria-hidden="true" className="dpe-live-arrow">
			<span>↗</span>
			<span>↗</span>
		</span>
	)
}

function HighlightArrow() {
	return (
		<svg
			aria-hidden="true"
			className="dpe-live-highlight-arrow"
			fill="none"
			height="13"
			viewBox="0 0 13 13"
			width="13"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d="M1 12L12.5 0.499965" />
			<path d="M1 0.5H12.5V12" />
		</svg>
	)
}

function HighlightLabel({ title }: { title: string }) {
	const lastSpace = title.lastIndexOf(' ')

	if (lastSpace === -1) {
		return (
			<span className="dpe-live-highlight-tail">
				{title}
				<HighlightArrow />
			</span>
		)
	}

	return (
		<>
			<span className="dpe-live-highlight-text">
				{title.slice(0, lastSpace + 1)}
			</span>
			<span className="dpe-live-highlight-tail">
				{title.slice(lastSpace + 1)}
				<HighlightArrow />
			</span>
		</>
	)
}

function ArrowRightIcon({ className = '' }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			className={className}
			fill="none"
			height="13"
			viewBox="0 0 13 13"
			width="13"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d="M6.72028 1.43542L12 6.71516L6.72044 11.9947" />
			<path d="M0 6.71424L11.9993 6.71442" />
		</svg>
	)
}

function ChevronDownIcon({ className = '' }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			className={className}
			fill="none"
			height="12"
			viewBox="0 0 22 12"
			width="22"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d="M1 1.12109L11 11.1211L21 1.12109" />
		</svg>
	)
}

function SplitRevealHeading({ text }: { text: string }) {
	const [isVisible, setIsVisible] = useState(false)
	const ref = useRef<HTMLHeadingElement>(null)

	useEffect(() => {
		const heading = ref.current
		if (!heading) return

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting) {
					setIsVisible(true)
					observer.disconnect()
				}
			},
			{ rootMargin: '0px 0px -20% 0px', threshold: 0.35 },
		)

		observer.observe(heading)

		return () => observer.disconnect()
	}, [])

	return (
		<h3
			aria-label={text}
			className={
				isVisible
					? 'dpe-live-split-heading is-visible'
					: 'dpe-live-split-heading'
			}
			ref={ref}
		>
			{text.split(' ').map((word, index, words) => (
				<span
					aria-hidden="true"
					className="dpe-live-split-word"
					key={`${word}-${index}`}
					style={{ '--word-index': index } as CSSProperties}
				>
					<span>
						{word}
						{index < words.length - 1 ? '\u00a0' : ''}
					</span>
				</span>
			))}
		</h3>
	)
}

function GridLines({ className = '' }: { className?: string }) {
	return (
		<div aria-hidden="true" className={`dpe-live-grid ${className}`}>
			<span />
			<span />
			<span />
			<span />
			<span />
		</div>
	)
}

function FooterArrowIcon() {
	return (
		<svg
			aria-hidden="true"
			className="dpe-live-newsletter-arrow"
			fill="none"
			viewBox="0 0 13 13"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d="M1 12L12.5 0.499965" />
			<path d="M1 0.5H12.5V12" />
		</svg>
	)
}

function ThemeSunIcon() {
	return (
		<svg
			aria-hidden="true"
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth="1.5"
			viewBox="0 0 24 24"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
		</svg>
	)
}

function ChevronUpDownIcon() {
	return (
		<svg
			aria-hidden="true"
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth="1.5"
			viewBox="0 0 24 24"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
		</svg>
	)
}

function TwitterIcon() {
	return (
		<svg fill="none" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M21.8496 22.7227H20.2051L9.4668 8.68359H11.2324L21.8496 22.7227Z"
				fill="white"
			/>
			<path
				clipRule="evenodd"
				d="M16 32C24.8359 32 32 24.8369 32 16C32 7.16309 24.8359 0 16 0C7.16406 0 0 7.16309 0 16C0 24.8369 7.16406 32 16 32ZM24.2168 7H21.248L16.3555 12.5918L12.125 7H6L13.3203 16.5713L6.38281 24.5H9.35352L14.707 18.3818L19.3867 24.5H25.3594L17.7305 14.4131L24.2168 7Z"
				fill="white"
				fillRule="evenodd"
			/>
		</svg>
	)
}

function DiscordIcon() {
	return (
		<svg fill="none" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M11.2129 18.8555C11.5352 19.1025 11.9258 19.248 12.3457 19.248C13.4512 19.248 14.3066 18.2744 14.3242 17.0752C14.3438 15.877 13.4551 14.8936 12.3418 14.8936C11.2285 14.8936 10.3633 15.877 10.3633 17.0752C10.3633 17.8115 10.7012 18.4629 11.2129 18.8555Z"
				fill="currentColor"
			/>
			<path
				d="M17.6758 17.0752C17.6758 18.2744 18.5684 19.248 19.6543 19.248C20.7598 19.248 21.6133 18.2744 21.6328 17.0752C21.6523 15.877 20.7695 14.8936 19.6543 14.8936C18.541 14.8936 17.6758 15.877 17.6758 17.0752Z"
				fill="currentColor"
			/>
			<path
				clipRule="evenodd"
				d="M16 32C24.8359 32 32 24.8369 32 16C32 7.16309 24.8359 0 16 0C7.16406 0 0 7.16309 0 16C0 24.8369 7.16406 32 16 32ZM19.0977 8C20.6641 8.2627 22.1914 8.72656 23.6367 9.38184C26.127 12.9844 27.3633 17.0479 26.9062 21.7363C25.2246 22.9678 23.3398 23.9023 21.3359 24.5C20.8848 23.9014 20.4863 23.2656 20.1445 22.5996C20.7969 22.3584 21.4258 22.0605 22.0254 21.7109C21.8691 21.6064 21.7148 21.4893 21.5645 21.3682C19.8242 22.1787 17.9238 22.5986 16 22.5986C14.0762 22.5986 12.1758 22.1787 10.4355 21.3682C10.2871 21.4814 10.1328 21.5977 9.97461 21.7109C10.2852 21.8926 10.6035 22.0596 10.9297 22.2119C11.2305 22.3535 11.5391 22.4824 11.8516 22.5977C11.5098 23.2637 11.1113 23.9004 10.6602 24.5C9.34961 24.1064 8.08984 23.5703 6.9043 22.9014C6.2793 22.5488 5.67383 22.1592 5.09375 21.7344C4.70312 17.6914 5.48242 13.5908 8.35547 9.38574C9.80273 8.73047 11.3281 8.26465 12.8965 8C13.1113 8.37988 13.3066 8.76953 13.4785 9.16992C14.3887 9.03418 15.3086 8.97266 16.2246 8.98535C16.9902 8.99512 17.7559 9.05664 18.5156 9.16992C18.6133 8.94434 18.7168 8.72266 18.8281 8.50391C18.9141 8.33398 19.0039 8.16602 19.0977 8Z"
				fill="currentColor"
				fillRule="evenodd"
			/>
		</svg>
	)
}

function YoutubeIcon() {
	return (
		<svg fill="none" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M13.625 19.1064V12.8848L19.959 15.9902L13.625 19.1064Z"
				fill="white"
			/>
			<path
				clipRule="evenodd"
				d="M16 32C24.8359 32 32 24.8369 32 16C32 7.16309 24.8359 0 16 0C7.16406 0 0 7.16309 0 16C0 24.8369 7.16406 32 16 32ZM22.0293 9.14355C19.1758 8.95215 12.8203 8.95312 9.9707 9.14355C6.88672 9.34961 6.52344 11.1807 6.5 16C6.52344 20.8105 6.88281 22.6494 9.9707 22.8564C12.8223 23.0469 19.1758 23.0479 22.0293 22.8564C25.1133 22.6504 25.4766 20.8193 25.5 16C25.4766 11.1895 25.1172 9.35059 22.0293 9.14355Z"
				fill="white"
				fillRule="evenodd"
			/>
		</svg>
	)
}

function InstagramIcon() {
	return (
		<svg fill="none" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M12.834 16.5049C12.834 18.2539 14.252 19.6709 16 19.6709C17.75 19.6709 19.166 18.2539 19.166 16.5049C19.166 14.7559 17.75 13.3379 16 13.3379C14.252 13.3379 12.834 14.7559 12.834 16.5049Z"
				fill="white"
			/>
			<path
				clipRule="evenodd"
				d="M17.7168 8.71191L16 8.70898C13.4629 8.70898 13.1621 8.71875 12.1602 8.76367C9.58398 8.88086 8.38477 10.1035 8.26562 12.6582C8.2207 13.6602 8.21094 13.96 8.21094 16.4971C8.21094 19.0342 8.2207 19.334 8.26562 20.335C8.38281 22.8857 9.58008 24.1123 12.1602 24.2305C13.1621 24.2764 13.4629 24.2861 16 24.2861C18.5371 24.2861 18.8359 24.2764 19.8379 24.2305C22.416 24.1133 23.6152 22.8896 23.7324 20.3359C23.7773 19.335 23.7871 19.0352 23.7871 16.4971C23.7871 13.9609 23.7793 13.6602 23.7324 12.6582C23.6152 10.1035 22.4141 8.88184 19.8379 8.76465C19.127 8.73242 18.7695 8.71777 17.7168 8.71191ZM15.998 11.6289C13.3027 11.6289 11.1191 13.8135 11.1191 16.5068C11.1191 19.2012 13.3027 21.3857 15.998 21.3857C18.6914 21.3857 20.875 19.2021 20.875 16.5068C20.875 13.8135 18.6914 11.6289 15.998 11.6289ZM21.0723 10.293C20.4414 10.293 19.9297 10.8037 19.9297 11.4326C19.9297 11.7832 20.0879 12.0967 20.3379 12.3057C20.5371 12.4727 20.793 12.5732 21.0723 12.5732C21.7012 12.5732 22.2109 12.0625 22.2109 11.4326C22.2109 10.8037 21.7012 10.293 21.0723 10.293Z"
				fill="white"
				fillRule="evenodd"
			/>
			<path
				clipRule="evenodd"
				d="M16 32C24.8359 32 32 24.8369 32 16C32 7.16309 24.8359 0 16 0C7.16406 0 0 7.16309 0 16C0 24.8369 7.16406 32 16 32ZM13.7266 7.00781C14.2324 7.00195 14.918 7 16 7C18.5801 7 18.9043 7.01074 19.918 7.05762C23.3711 7.21582 25.2871 9.13672 25.4434 12.584C25.4883 13.5967 25.5 13.9199 25.5 16.5C25.5 19.0801 25.4883 19.4043 25.4414 20.417C25.2871 23.8701 23.3633 25.7842 19.918 25.9434C18.9043 25.9893 18.5801 26 16 26C13.4199 26 13.0977 25.9893 12.082 25.9434C8.62891 25.7842 6.71484 23.8672 6.55664 20.417C6.51172 19.4043 6.5 19.0801 6.5 16.5C6.5 13.9199 6.51172 13.5967 6.55859 12.583C6.7168 9.12988 8.63281 7.21582 12.084 7.05664C12.6719 7.03027 13.0273 7.01562 13.7266 7.00781Z"
				fill="white"
				fillRule="evenodd"
			/>
		</svg>
	)
}

function Payload3DFooter() {
	const containerRef = useRef<HTMLDivElement>(null)
	const [gradientStyles, setGradientStyles] = useState<CSSProperties>({
		'--mouse-x': -999,
		'--mouse-y': -999,
	} as CSSProperties)

	useEffect(() => {
		const container = containerRef.current
		if (!container) return

		let frame = 0

		const updateMousePosition = (event: MouseEvent) => {
			if (frame) return

			frame = window.requestAnimationFrame(() => {
				const bounds = container.getBoundingClientRect()

				setGradientStyles({
					'--mouse-x': event.clientX - bounds.left,
					'--mouse-y': event.clientY - bounds.top,
				} as CSSProperties)

				frame = 0
			})
		}

		const observer = new IntersectionObserver(([entry]) => {
			if (entry?.isIntersecting) {
				window.addEventListener('mousemove', updateMousePosition)
			} else {
				window.removeEventListener('mousemove', updateMousePosition)
			}
		})

		observer.observe(container)

		return () => {
			observer.disconnect()
			window.removeEventListener('mousemove', updateMousePosition)
			if (frame) window.cancelAnimationFrame(frame)
		}
	}, [])

	return (
		<div className="dpe-live-3d-wrap">
			<div className="dpe-live-3d" data-theme="light" ref={containerRef}>
				<div className="dpe-live-3d-mask">
					<div className="dpe-live-3d-noise" />
					<div className="dpe-live-3d-gradient" style={gradientStyles} />
				</div>
			</div>
		</div>
	)
}

export function BackgroundGradient() {
	return (
		<div
			aria-hidden="true"
			className="dpe-live-background-gradient dpe-live-background-gradient--light"
		>
			<video
				autoPlay
				loop
				muted
				playsInline
				poster={mediaAsset(
					'/images/payload-home/glass-animation-poster-v1.jpg',
				)}
				preload="metadata"
			>
				<source
					src={`${videoPath}/glass-animation-optimized-v1.mp4`}
					type="video/mp4"
				/>
			</video>
		</div>
	)
}

function NavLinkId(label: string) {
	return `Nav-${label
		.replace(/&/g, 'and')
		.replace(/[^a-zA-Z0-9]+/g, '-')
		.replace(/^-|-$/g, '')}`
}

function NavArrowIcon({ className = '' }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			className={className}
			fill="none"
			height="13"
			viewBox="0 0 13 13"
			width="13"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d="M1 12L12.5 0.499965" />
			<path d="M1 0.5H12.5V12" />
		</svg>
	)
}

function DesktopNavMenu({
	activeDropdownItem,
	activeTab,
	dropdownRef,
	index,
	item,
	onDropdownItemEnter,
	onMouseEnter,
	onMouseLeave,
	onReset,
	tabRef,
}: {
	activeDropdownItem: number | undefined
	activeTab: number | undefined
	dropdownRef: (ref: HTMLDivElement | null) => void
	index: number
	item: NavItem
	onDropdownItemEnter: (index: number) => void
	onMouseEnter: () => void
	onMouseLeave: () => void
	onReset: () => void
	tabRef: (ref: HTMLElement | null) => void
}) {
	const hasDropdown = Boolean(item.sections)

	return (
		<div
			className="dpe-live-nav-item"
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			{hasDropdown ? (
				<button className="dpe-live-nav-tab" ref={tabRef} type="button">
					<span className="dpe-live-nav-link">{item.label}</span>
				</button>
			) : (
				<SmartLink
					className="dpe-live-nav-link"
					href={item.href}
					linkRef={tabRef}
				>
					{item.label}
				</SmartLink>
			)}
			{hasDropdown ? (
				<div
					className={[
						'dpe-live-mega',
						// Remove this modifier when the dropdowns have multiple columns again.
						item.sections?.length === 1 ? 'dpe-live-mega-sparse' : '',
						activeTab === index ? 'dpe-live-mega-active' : '',
					]
						.filter(Boolean)
						.join(' ')}
					onClick={onReset}
					ref={dropdownRef}
				>
					<div className="dpe-live-mega-intro">
						<p>{item.description}</p>
						{/* Dropdown intro CTA hidden for now; restore when the right-side content is redesigned.
						{item.label === 'Product' || item.label === 'Enterprise' ? (
							<SmartLink
								className="dpe-live-mega-action"
								href="/book-demo"
								id={NavLinkId('Schedule a Demo')}
							>
								Schedule a Demo
								<NavArrowIcon className="dpe-live-link-arrow" />
							</SmartLink>
						) : null}
						*/}
					</div>
					{item.sections?.map((section, sectionIndex) => (
						<div
							className={[
								'dpe-live-mega-column',
								activeDropdownItem === sectionIndex
									? 'dpe-live-mega-column-active'
									: '',
							]
								.filter(Boolean)
								.join(' ')}
							key={section.label}
							onMouseEnter={() => onDropdownItemEnter(sectionIndex)}
							style={
								{
									'--dpe-live-mega-link-count': section.links.length,
								} as CSSProperties
							}
						>
							{section.hideLabel ? null : <span>{section.label}</span>}
							{section.links.map((link) => (
								<SmartLink
									href={link.href}
									id={NavLinkId(link.label)}
									key={link.label}
								>
									{link.label}
								</SmartLink>
							))}
						</div>
					))}
					{/*TODO: ADD BACK Later
				{item.visualTiles ? (
					<div aria-hidden="true" className="dpe-live-mega-visual">
						{item.visualTiles.map((tile) => (
							<div className="dpe-live-mega-visual-tile" key={tile.label}>
								<span>{tile.label}</span>
							</div>
						))}
					</div>
				) : null}
				*/}
					{/* Featured right-side promo hidden for now.
					{item.featured ? (
						<div className="dpe-live-mega-feature">
							<span>{item.featured.eyebrow}</span>
							<h4>{item.featured.title}</h4>
							<div className="dpe-live-mega-feature-actions">
								{item.featured.actions?.map((action) => (
									<SmartLink
										className="dpe-live-mega-action"
										href={action.href}
										id={NavLinkId(action.label)}
										key={action.label}
									>
										{action.label}
										<NavArrowIcon className="dpe-live-link-arrow" />
									</SmartLink>
								))}
							</div>
						</div>
					) : null}
					*/}
				</div>
			) : null}
		</div>
	)
}

function MobileMenuItems({
	onClose,
	onOpenSubMenu,
}: {
	onClose: () => void
	onOpenSubMenu: (index: number) => void
}) {
	return (
		<ul className="dpe-live-mobile-menu-items">
			{navItems.map((item, index) =>
				item.sections ? (
					<button
						className="dpe-live-mobile-menu-item"
						key={item.label}
						onClick={() => onOpenSubMenu(index)}
						type="button"
					>
						{item.label}
						<NavArrowIcon className="dpe-live-mobile-item-arrow" />
					</button>
				) : (
					<SmartLink
						className="dpe-live-mobile-menu-item"
						href={item.href}
						key={item.label}
						onClick={onClose}
					>
						{item.label}
					</SmartLink>
				),
			)}
			<Link
				className="dpe-live-mobile-menu-item dpe-live-mobile-new-project"
				onClick={onClose}
				to="/book-demo"
			>
				Get a Demo
			</Link>
			<Crosshair className="dpe-live-crosshair-top-left" />
			<Crosshair className="dpe-live-crosshair-bottom-left" />
		</ul>
	)
}

function MobileSubMenu({
	activeIndex,
	onBack,
	onClose,
	open,
}: {
	activeIndex: number | undefined
	onBack: () => void
	onClose: () => void
	open: boolean
}) {
	const item = activeIndex === undefined ? undefined : navItems[activeIndex]

	return (
		<div
			aria-hidden={!open}
			className={[
				'dpe-live-mobile-modal',
				'dpe-live-mobile-submenu',
				open ? 'is-open' : '',
			]
				.filter(Boolean)
				.join(' ')}
			onClick={onClose}
		>
			<div
				className="dpe-live-mobile-submenu-wrap"
				onClick={(event) => event.stopPropagation()}
			>
				{item ? (
					<div className="dpe-live-mobile-submenu-items">
						<button
							className="dpe-live-mobile-back"
							onClick={onBack}
							type="button"
						>
							<NavArrowIcon className="dpe-live-mobile-back-arrow" />
							Back
							<Crosshair className="dpe-live-crosshair-top-left" />
						</button>
						{/* Mobile intro CTA hidden for now; restore when submenu promo content is redesigned.
						{item.label === 'Product' || item.label === 'Enterprise' ? (
							<div className="dpe-live-mobile-description-links">
								<SmartLink
									className="dpe-live-mobile-description-link"
									href="/book-demo"
									id={NavLinkId('Schedule a Demo')}
									onClick={onClose}
								>
									Schedule a Demo
									<NavArrowIcon className="dpe-live-link-arrow" />
								</SmartLink>
							</div>
						) : null}
						*/}
						{item.sections?.map((section) => (
							<div className="dpe-live-mobile-link-wrap" key={section.label}>
								{section.hideLabel ? null : (
									<div className="dpe-live-mobile-tag">{section.label}</div>
								)}
								<div className="dpe-live-mobile-list-wrap">
									{section.links.map((link) => (
										<SmartLink
											className="dpe-live-mobile-submenu-link"
											href={link.href}
											id={NavLinkId(link.label)}
											key={link.label}
											onClick={onClose}
										>
											{link.label}
										</SmartLink>
									))}
								</div>
							</div>
						))}
						{/* Mobile featured promo hidden for now.
						{item.featured ? (
							<div className="dpe-live-mobile-link-wrap">
								<div className="dpe-live-mobile-tag">
									{item.featured.eyebrow}
								</div>
								<div className="dpe-live-mobile-featured-label">
									<h4>{item.featured.title}</h4>
								</div>
								<div className="dpe-live-mobile-featured-actions">
									{item.featured.actions?.map((action) => (
										<SmartLink
											className="dpe-live-mobile-featured-link"
											href={action.href}
											id={NavLinkId(action.label)}
											key={action.label}
											onClick={onClose}
										>
											{action.label}
											<NavArrowIcon className="dpe-live-link-arrow" />
										</SmartLink>
									))}
								</div>
							</div>
						) : null}
						*/}
						<Crosshair className="dpe-live-crosshair-bottom-left" />
					</div>
				) : null}
			</div>
			<div className="dpe-live-mobile-modal-blur" />
		</div>
	)
}

export function PayloadHeader() {
	const [mobileOpen, setMobileOpen] = useState(false)
	const [mobileSubMenuOpen, setMobileSubMenuOpen] = useState(false)
	const [activeMobileTab, setActiveMobileTab] = useState<number | undefined>()
	const [activeTab, setActiveTab] = useState<number | undefined>()
	const [activeDropdown, setActiveDropdown] = useState(false)
	const [activeDropdownItem, setActiveDropdownItem] = useState<
		number | undefined
	>()
	const [backgroundHeight, setBackgroundHeight] = useState('0px')
	const [underlineStyles, setUnderlineStyles] = useState<CSSProperties>({
		opacity: 0,
	})
	const menuItemRefs = useRef<(HTMLElement | null)[]>([])
	const dropdownMenuRefs = useRef<(HTMLDivElement | null)[]>([])
	const hoverTimeout = useRef<number | null>(null)
	const chromeRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		const root = document.documentElement
		let lastHidden: boolean | null = null

		const apply = () => {
			const hidden = window.scrollY > 30
			if (hidden === lastHidden) return
			lastHidden = hidden
			root.style.setProperty(
				'--payload-visible-topbar-height',
				hidden ? '0px' : 'var(--payload-topbar-height)',
			)
			chromeRef.current?.classList.toggle(
				'dpe-live-chrome-topbar-hidden',
				hidden,
			)
		}

		apply()
		window.addEventListener('scroll', apply, { passive: true })

		return () => {
			window.removeEventListener('scroll', apply)
			root.style.removeProperty('--payload-visible-topbar-height')
		}
	}, [])

	useEffect(() => {
		if (!mobileOpen) return

		const scrollY = window.scrollY
		const { body, documentElement } = document
		const previousBodyOverflow = body.style.overflow
		const previousBodyOverscrollBehavior = body.style.overscrollBehavior
		const previousDocumentOverflow = documentElement.style.overflow
		const previousDocumentOverscrollBehavior =
			documentElement.style.overscrollBehavior

		documentElement.style.overflow = 'hidden'
		documentElement.style.overscrollBehavior = 'none'
		body.style.overflow = 'hidden'
		body.style.overscrollBehavior = 'none'

		return () => {
			documentElement.style.overflow = previousDocumentOverflow
			documentElement.style.overscrollBehavior =
				previousDocumentOverscrollBehavior
			body.style.overflow = previousBodyOverflow
			body.style.overscrollBehavior = previousBodyOverscrollBehavior
			window.scrollTo(0, scrollY)
		}
	}, [mobileOpen])

	useEffect(() => {
		const mediaQuery = window.matchMedia('(min-width: 1025px)')

		const handleViewportChange = () => {
			if (mediaQuery.matches) {
				closeMobile()
			}
		}

		handleViewportChange()
		mediaQuery.addEventListener('change', handleViewportChange)

		return () => mediaQuery.removeEventListener('change', handleViewportChange)
	}, [])

	const resetHoverStyles = () => {
		if (hoverTimeout.current) {
			clearTimeout(hoverTimeout.current)
			hoverTimeout.current = null
		}
		setActiveDropdown(false)
		setActiveTab(undefined)
		setActiveDropdownItem(undefined)
		setBackgroundHeight('0px')
		setUnderlineStyles((current) => ({ ...current, opacity: 0 }))
	}

	const handleHoverEnter = (index: number) => {
		const hoveredMenuItem = menuItemRefs.current[index]
		const hoveredDropdownMenu = dropdownMenuRefs.current[index]
		const bgHeight = hoveredDropdownMenu?.clientHeight || 0

		setActiveTab(index)
		setActiveDropdown(bgHeight > 0)
		setActiveDropdownItem(undefined)

		if (hoveredMenuItem) {
			setUnderlineStyles({
				left: hoveredMenuItem.offsetLeft,
				opacity: 1,
				width: hoveredMenuItem.clientWidth,
			})
		}

		setBackgroundHeight(bgHeight === 0 ? '0px' : `${bgHeight}px`)
	}

	const handleMouseEnter = (index: number) => {
		if (!activeDropdown) {
			hoverTimeout.current = window.setTimeout(() => {
				handleHoverEnter(index)
			}, 200)
		} else {
			handleHoverEnter(index)
		}
	}

	const handleMouseLeave = () => {
		if (hoverTimeout.current) {
			clearTimeout(hoverTimeout.current)
			hoverTimeout.current = null
		}
	}

	const closeMobile = () => {
		setMobileOpen(false)
		setMobileSubMenuOpen(false)
		setActiveMobileTab(undefined)
	}

	const openMobileSubMenu = (index: number) => {
		setActiveMobileTab(index)
		setMobileSubMenuOpen(true)
	}

	return (
		<div
			ref={chromeRef}
			className={[
				'dpe-live-chrome',
				mobileOpen ? 'dpe-live-chrome-mobile-open' : '',
			]
				.filter(Boolean)
				.join(' ')}
		>
			<div className="dpe-live-topbar-wrapper" id="topBar">
				<Link className="dpe-live-topbar" to="/limited-time-offer">
					<span>Limited time offer for 50+ inventory dealers</span>
					<span className="dpe-live-topbar-action">
						<span className="dpe-live-topbar-action-text">
							Get two months free
						</span>{' '}
						<Arrow />
					</span>
				</Link>
			</div>
			<header
				className={[
					'dpe-live-header',
					activeDropdown ? 'dpe-live-header-active' : '',
					mobileOpen ? 'dpe-live-mobile-open' : '',
				]
					.filter(Boolean)
					.join(' ')}
			>
				<Link
					aria-label="Dealer Pro Edge home"
					className="dpe-live-logo-link"
					to="/"
				>
					<PayloadLogo />
				</Link>
				<nav
					aria-label="Primary"
					className="dpe-live-nav"
					onMouseLeave={resetHoverStyles}
				>
					{navItems.map((item, index) => (
						<DesktopNavMenu
							activeDropdownItem={activeDropdownItem}
							activeTab={activeTab}
							dropdownRef={(ref) => {
								dropdownMenuRefs.current[index] = ref
							}}
							index={index}
							item={item}
							key={item.label}
							onDropdownItemEnter={setActiveDropdownItem}
							onMouseEnter={() => handleMouseEnter(index)}
							onMouseLeave={handleMouseLeave}
							onReset={resetHoverStyles}
							tabRef={(ref) => {
								menuItemRefs.current[index] = ref
							}}
						/>
					))}
					<div
						aria-hidden="true"
						className="dpe-live-nav-underline"
						style={underlineStyles}
					>
						<div />
					</div>
				</nav>
				<div className="dpe-live-actions">
					<Link className="dpe-live-start" to="/book-demo">
						Get a Demo
					</Link>
					<button
						aria-expanded={mobileOpen}
						aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
						className={['dpe-live-menu', mobileOpen ? 'dpe-live-menu-open' : '']
							.filter(Boolean)
							.join(' ')}
						onClick={() => {
							setMobileOpen((open) => !open)
							setMobileSubMenuOpen(false)
							setActiveMobileTab(undefined)
						}}
						type="button"
					>
						<span />
						<span />
						<span />
					</button>
				</div>
				<div
					aria-hidden="true"
					className="dpe-live-nav-background"
					style={{ height: backgroundHeight }}
				/>
			</header>
			<div
				aria-hidden={!mobileOpen}
				className={['dpe-live-mobile-modal', mobileOpen ? 'is-open' : '']
					.filter(Boolean)
					.join(' ')}
			>
				<div className="dpe-live-mobile-wrap">
					<MobileMenuItems
						onClose={closeMobile}
						onOpenSubMenu={openMobileSubMenu}
					/>
				</div>
				<div className="dpe-live-mobile-modal-blur" />
			</div>
			<MobileSubMenu
				activeIndex={activeMobileTab}
				onBack={() => setMobileSubMenuOpen(false)}
				onClose={closeMobile}
				open={mobileSubMenuOpen}
			/>
		</div>
	)
}

function CommandButton() {
	return (
		<Link className="dpe-live-command" to="/limited-time-offer">
			<AnimatedButtonLabel>Limited Time Offer</AnimatedButtonLabel>
			<TimerIcon />
		</Link>
	)
}

function AnimatedButtonLabel({ children }: { children: ReactNode }) {
	return (
		<span className="dpe-live-button-label">
			<span>{children}</span>
			<span aria-hidden="true">{children}</span>
		</span>
	)
}

function DemoButton({
	className = 'dpe-live-demo',
	label = 'Get a Demo',
	to = '/book-demo',
}: {
	className?: string
	label?: string
	to?: string
}) {
	return (
		<Link className={className} to={to}>
			<AnimatedButtonLabel>{label}</AnimatedButtonLabel>
			<Arrow />
		</Link>
	)
}

function TimerIcon() {
	return (
		<span aria-hidden="true" className="dpe-live-timer-icon">
			<span>
				<svg fill="none" viewBox="0 0 24 24">
					<path d="M9 2h6" />
					<path d="M12 6v6l4 2" />
					<circle cx="12" cy="13" r="8" />
				</svg>
			</span>
			<span>
				<svg fill="none" viewBox="0 0 24 24">
					<path d="M9 2h6" />
					<path d="M12 6v6l4 2" />
					<circle cx="12" cy="13" r="8" />
				</svg>
			</span>
		</span>
	)
}

function HeroSection() {
	return (
		<section className="dpe-live-hero">
			<GridLines />
			<div className="dpe-live-hero-inner">
				<div className="dpe-live-hero-copy">
					<h1>
						Your dealership’s competitive edge starts with a faster website.
					</h1>
					<div className="dpe-live-hero-buttons">
						<CommandButton />
						<DemoButton />
					</div>
				</div>
				<div aria-hidden="true" className="dpe-live-hero-media">
					<MediaImage
						alt=""
						aspectRatio={16 / 10}
						className="dpe-live-device-preview"
						layout="fullWidth"
						priority
						src="/images/payload-home/mac-and-iphone-laptop.png"
					/>
				</div>
			</div>
		</section>
	)
}

function Crosshair({ className }: { className: string }) {
	return (
		<svg
			aria-hidden="true"
			className={`dpe-live-crosshair ${className}`}
			fill="none"
			height="21"
			stroke="currentColor"
			viewBox="0 0 20 21"
			width="20"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d="M10 0.332031V20.332" />
			<path d="M0 10.332L20 10.332" />
		</svg>
	)
}

function LogoShowcaseSection() {
	const visibleLogos = logoShowcase.slice(0, logoShowcaseCellCount)
	// TODO: Add the one-at-a-time logo animation back when there are more than six approved logos to rotate through.

	return (
		<section className="dpe-live-logo-showcase">
			<GridLines />
			<p>
				DealerProEdge powers websites for dealerships and dealer groups ready to
				dominate their market.
			</p>
			<div className="dpe-live-logo-grid">
				{Array.from({ length: logoShowcaseCellCount }).map((_, cellIndex) => {
					const logo = visibleLogos[cellIndex]

					return (
						<div
							className={['dpe-live-logo-item', logo && 'dpe-live-logo-present']
								.filter(Boolean)
								.join(' ')}
							key={cellIndex}
						>
							<div className="dpe-live-logo-content">
								{logo ? (
									<div>
										{isSvgMedia(logo.src) ? (
											<img
												alt={logo.alt}
												className={['dpe-live-dealer-logo', logo.className]
													.filter(Boolean)
													.join(' ')}
												decoding="async"
												loading="eager"
												src={logo.src}
											/>
										) : (
											<MediaImage
												alt={logo.alt}
												aspectRatio={3}
												className={['dpe-live-dealer-logo', logo.className]
													.filter(Boolean)
													.join(' ')}
												fit="contain"
												layout="fullWidth"
												priority
												src={logo.src}
											/>
										)}
									</div>
								) : null}
							</div>
						</div>
					)
				})}
				<Crosshair className="dpe-live-crosshair-top" />
				<Crosshair className="dpe-live-crosshair-bottom" />
			</div>
		</section>
	)
}

function IntroSection() {
	return (
		<section className="dpe-live-intro">
			<GridLines />
			<div className="dpe-live-statement-wrap">
				<div className="dpe-live-statement-content">
					<h2>
						Inventory-first for shoppers.
						<br />
						Sales-ready for dealers.
					</h2>
				</div>
				<div className="dpe-live-statement-asset">
					<MediaImage
						alt="DealerProEdge vehicle inventory management dashboard."
						aspectRatio={16 / 10}
						className="dpe-live-intro-collage"
						fit="contain"
						layout="fullWidth"
						src="/images/payload-home/vehicle_inventory_dashboard_actual_transparent.png"
					/>
				</div>
			</div>
		</section>
	)
}

function UseCasesSection() {
	const [active, setActive] = useState(0)

	return (
		<section className="dpe-live-usecases">
			<GridLines />
			<div className="dpe-live-highlight-wrap">
				<span className="dpe-live-section-label">
					AI-ready today. Dealer-ready every day.
				</span>
				<div className="dpe-live-highlight-list">
					{useCases.map((item, index) => (
						<div
							aria-current={index === active ? 'true' : undefined}
							className={[
								'dpe-live-highlight',
								index < active ? 'dpe-live-before-active' : '',
								index === active ? 'dpe-live-active-highlight' : '',
								index > active ? 'dpe-live-after-active' : '',
							]
								.filter(Boolean)
								.join(' ')}
							key={item.title}
							onFocus={() => setActive(index)}
							onMouseEnter={() => setActive(index)}
						>
							<span aria-disabled="true" className="dpe-live-highlight-link">
								<HighlightLabel title={item.title} />
							</span>
							<div className="dpe-live-highlight-media-top">
								{item.topImage ? (
									<MediaImage
										alt={item.alt}
										aspectRatio={16 / 10}
										layout="fullWidth"
										priority
										src={item.topImage}
									/>
								) : null}
							</div>
							<div className="dpe-live-highlight-media-bottom">
								{item.bottomImage ? (
									<MediaImage
										alt={item.bottomAlt ?? item.alt}
										aspectRatio={16 / 10}
										layout="fullWidth"
										priority
										src={item.bottomImage}
									/>
								) : null}
							</div>
						</div>
					))}
				</div>
				<span
					aria-disabled="true"
					className="dpe-live-demo dpe-live-section-demo"
				>
					<AnimatedButtonLabel>Schedule a demo</AnimatedButtonLabel>
					<Arrow />
				</span>
			</div>
		</section>
	)
}

function FutureSection() {
	const [active, setActive] = useState(0)

	return (
		<section className="dpe-live-future">
			<GridLines />
			<div className="dpe-live-future-copy">
				<SplitRevealHeading text="Meet the website backend built to sell cars" />
				<div className="dpe-live-future-tabs">
					{futureCards.map((card, index) => (
						<div
							className={
								active === index
									? 'dpe-live-future-panel is-active'
									: 'dpe-live-future-panel'
							}
							key={card.title}
						>
							<button
								aria-controls={`dpe-live-future-content-${index}`}
								aria-expanded={active === index}
								id={`dpe-live-future-toggle-${index}`}
								onClick={() => setActive(index)}
								type="button"
							>
								<span className="dpe-live-future-title">{card.title}</span>
								<ChevronDownIcon className="dpe-live-future-chevron" />
							</button>
							<div
								aria-hidden={active !== index}
								aria-labelledby={`dpe-live-future-toggle-${index}`}
								className="dpe-live-future-content"
								id={`dpe-live-future-content-${index}`}
								role="region"
							>
								<div className="dpe-live-future-content-inner">
									<p>{card.text}</p>
									{/* //TODO: Put back when we have a page for them again
									<Link
										className="dpe-live-future-link"
										tabIndex={active === index ? undefined : -1}
										to="/docs"
									>
										Learn more
										<ArrowRightIcon className="dpe-live-future-arrow" />
									</Link>
									*/}
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
			<div className="dpe-live-future-media">
				{futureCards.map((card, index) => (
					<MediaImage
						alt={card.alt}
						aspectRatio={16 / 10}
						className={active === index ? 'is-active' : ''}
						key={card.title}
						layout="fullWidth"
						src={mediaAsset(`/images/payload-home/${card.image}`)}
					/>
				))}
			</div>
		</section>
	)
}

function ManifestoSection() {
	return (
		<section className="dpe-live-manifesto">
			<GridLines />
			<div>
				<h2>We’re building a better way for dealers.</h2>
				<p>
					Too many dealership websites make big promises, then slow shoppers
					down with cluttered pages, generic inventory experiences, and tools
					that feel bolted on instead of built for automotive retail.
				</p>
				<p>DealerProEdge gives dealers a sharper path forward.</p>
				<p>
					Your website should load fast, rank for the vehicles and services your
					market is searching for, and turn shoppers into real conversations. We
					combine performance-first design, inventory-aware SEO, and connected
					lead tools so your team can move quickly without waiting on a vendor
					for every change.
				</p>
				<p>
					With DealerProEdge, build a dealership website that works like it’s
					yours.
				</p>
				<div className="dpe-live-manifesto-actions">
					<DemoButton label="Schedule a Demo" />
					<Link
						className="dpe-live-demo dpe-live-manifesto-placeholder"
						to="/book-demo"
					>
						<AnimatedButtonLabel>Get Started</AnimatedButtonLabel>
						<Arrow />
					</Link>
				</div>
			</div>
		</section>
	)
}

export function PayloadFooter() {
	return (
		<footer className="dpe-live-footer" data-theme="light">
			<GridLines />
			<div className="dpe-live-footer-container">
				<div className="dpe-live-footer-columns">
					{footerColumns.map((column) => (
						<div className="dpe-live-footer-column" key={column.label}>
							<p>{column.label}</p>
							<div className="dpe-live-footer-list">
								{column.links.map((link) => (
									<SmartLink
										className="dpe-live-footer-link"
										href={link.href}
										key={link.label}
										newTab={link.href.startsWith('http')}
									>
										{link.label}
									</SmartLink>
								))}
							</div>
						</div>
					))}
					{/* Stay connected footer column hidden for now.
					<div className="dpe-live-footer-column dpe-live-footer-connected">
						<p>Stay connected</p>
						<form
							className="dpe-live-newsletter"
							noValidate
							onSubmit={handleNewsletterSubmit}
						>
							<div className="dpe-live-newsletter-field">
								<label
									className="dpe-live-visually-hidden"
									htmlFor={newsletterId}
								>
									Subscribe to our newsletter
								</label>
								<input
									autoCapitalize="none"
									autoComplete="off"
									autoCorrect="off"
									id={newsletterId}
									name="email"
									onChange={(event) => setEmail(event.currentTarget.value)}
									placeholder="Enter your email"
									type="email"
									value={email}
								/>
								<button disabled={!canSubmitNewsletter} type="submit">
									<FooterArrowIcon />
									<span className="dpe-live-visually-hidden">Submit</span>
								</button>
							</div>
						</form>
						<div className="dpe-live-socials">
							{socialLinks.map((link) => (
								<a
									aria-label={link.label}
									href={link.href}
									key={link.label}
									rel="noopener noreferrer"
									target="_blank"
								>
									<SocialIcon icon={link.icon} />
								</a>
							))}
						</div>
						<div className="dpe-live-theme">
							<label className="dpe-live-visually-hidden" htmlFor={themeId}>
								Switch themes
							</label>
							<span className="dpe-live-theme-icon">
								<ThemeSunIcon />
							</span>
							<select defaultValue="auto" id={themeId}>
								<option value="auto">Auto</option>
								<option value="light">Light</option>
								<option value="dark">Dark</option>
							</select>
							<span className="dpe-live-theme-chevron">
								<ChevronUpDownIcon />
							</span>
						</div>
					</div>
					*/}
				</div>
			</div>
			<Payload3DFooter />
		</footer>
	)
}

export default function HomePage() {
	return (
		<div className="dpe-live-page dpe-live-page--light">
			<BackgroundGradient />
			<PayloadHeader />
			<main>
				<HeroSection />
				<LogoShowcaseSection />
				<UseCasesSection />
				<FutureSection />
				<ManifestoSection />
			</main>
			<PayloadFooter />
		</div>
	)
}
